import { IResultError } from '../interfaces/IProgram';

export class BackendError extends Error {
  status_code: string | number | null;
  message: string;
  message_translation_key?: string;
  constructor(
    _status_code: string | number | null,
    _message: string,
    _message_translation_key?: string,
  ) {
    super();
    this.status_code = _status_code;
    this.message = _message;
    this.message_translation_key = _message_translation_key;
  }
}

export const isBackendError = (error: any): error is BackendError => {
  return error.status_code && !Number.isNaN(error.status_code) && error.message;
};

/**
 * Verifica si la respuesta es un ResultError (Union type de GraphQL)
 * @param response - La respuesta de GraphQL que puede ser un objeto de datos o ResultError
 * @returns true si es ResultError, false en caso contrario
 */
export const isResultError = (response: any): response is IResultError => {
  return response && response.__typename === 'ResultError';
};

/**
 * Procesa una respuesta GraphQL que puede ser un dato exitoso o un ResultError
 * @param response - La respuesta de GraphQL
 * @returns Un objeto { success: boolean; data?: T; error?: IResultError }
 */
export const processGraphQLResponse = <T>(
  response: T | IResultError | null | undefined,
): {
  success: boolean;
  data?: T;
  error?: IResultError;
} => {
  if (!response) {
    return { success: false };
  }

  if (isResultError(response)) {
    return {
      success: false,
      error: response,
    };
  }

  return {
    success: true,
    data: response as T,
  };
};

/**
 * Filtra los items de una lista que son ResultError
 * Útil para procesar listas que pueden contener errores
 * @param items - Array de items que pueden ser datos o ResultError
 * @returns Un objeto con los items exitosos y los errores separados
 */
export const filterGraphQLListResponse = <T>(
  items: (T | IResultError)[],
): {
  data: T[];
  errors: IResultError[];
} => {
  const data: T[] = [];
  const errors: IResultError[] = [];

  items.forEach((item) => {
    if (isResultError(item)) {
      errors.push(item);
    } else {
      data.push(item);
    }
  });

  return { data, errors };
};

/**
 * Obtiene el mensaje de error de un ResultError
 * @param error - El error de tipo ResultError
 * @param defaultMessage - Mensaje por defecto si no hay mensaje en el error
 * @returns El mensaje de error
 */
export const getResultErrorMessage = (
  error: IResultError,
  defaultMessage = 'Ha ocurrido un error',
): string => {
  return error.message || defaultMessage;
};

/**
 * Obtiene la clave de traducción del mensaje de error
 * @param error - El error de tipo ResultError
 * @returns La clave de traducción del error o undefined
 */
export const getResultErrorTranslationKey = (
  error: IResultError,
): string | undefined => {
  return error.message_translation_key;
};
